import { AbacPredicate, Rules } from "../interfaces";
import ensureArray from "./ensureArray";
import roleHasPermission from "./roleHasPermission";

/**
 * Checks if the user's roles have all required permissions
 * @param rules - The complete rules configuration
 * @param roles - Array of roles assigned to the user
 * @param permissions - Array of direct permissions granted to the user
 * @param requiredPermissions - Permission(s) to verify
 * @param user - User context for evaluation
 * @param data - Data context for evaluation
 * @returns true if all required permissions are satisfied, false otherwise
 */
const rolesHavePermissions = <Role extends string, Permission extends string, User, Data>(
  rules: Rules<Role, Permission, User>,
  roles: Role[],
  permissions: Permission[],
  requiredPermissions: Permission[] | Permission,
  user?: User,
  data?: Data,
): boolean =>
  // For *every* required permission
  ensureArray(requiredPermissions).every(
    (permission) =>
      // Check that *some* role has that permission
      roles.some((role) => roleHasPermission(rules, role, permission, user, data)) ||
      // Or this specific permission is granted directly
      Boolean(
        permissions.includes(permission)
          ? typeof rules[permission] === "function"
            ? // Is ABAC permission with predicate
              (rules[permission] as AbacPredicate<User, Data>)(data, user)
            : // Permission doesn't need a predicate
              true
          : // Permission not included
            false,
      ),
  );

export default rolesHavePermissions;
